import { useState, useEffect } from 'react';
import { Search, Bell, FileText, Download, Filter } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AuditLogEntry, AgentInteraction, GuardrailTrigger } from '../../types/agent';
import { MetricCard, Badge, EmptyState } from '../shared';

type EventTypeFilter = 'all' | 'interaction' | 'guardrail_trigger' | 'escalation' | 'config_change' | 'api_call';

export function AuditLog() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [interactions, setInteractions] = useState<AgentInteraction[]>([]);
  const [triggers, setTriggers] = useState<GuardrailTrigger[]>([]);
  const [filterType, setFilterType] = useState<EventTypeFilter>('all');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load audit log entries
      const auditQuery = query(
        collection(db, 'auditLog'),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(500)
      );
      const auditSnap = await getDocs(auditQuery);
      setAuditEntries(auditSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AuditLogEntry));

      // Load interactions for stats
      const interactionsQuery = query(
        collection(db, 'agentInteractions'),
        where('userId', '==', user.uid)
      );
      const interactionsSnap = await getDocs(interactionsQuery);
      setInteractions(interactionsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AgentInteraction));

      // Load guardrail triggers for stats
      const triggersQuery = query(
        collection(db, 'guardrailTriggers'),
        where('userId', '==', user.uid)
      );
      const triggersSnap = await getDocs(triggersQuery);
      setTriggers(triggersSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as GuardrailTrigger));

    } catch (error) {
      console.error('Error loading audit log:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Event Type', 'Description', 'Actor'];
    const rows = filteredEntries.map(entry => [
      entry.timestamp.toDate().toLocaleString(),
      entry.eventType,
      entry.description,
      entry.actor || 'System'
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalInteractions = interactions.length;
  const totalTriggers = triggers.filter(t => t.action !== 'allow_with_warning').length;
  const passedChecks = interactions.length - totalTriggers;
  const escalations = triggers.filter(t => t.action === 'escalate').length;

  const filteredEntries =
    filterType === 'all'
      ? auditEntries
      : auditEntries.filter(e => e.eventType === filterType);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Audit Log</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search logs..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
            <h1 className="text-3xl font-semibold text-gray-900">Audit Log</h1>
            <p className="text-sm text-gray-600 mt-1">Complete audit trail for compliance and security monitoring</p>
          </div>

          <button
            onClick={exportToCSV}
            disabled={filteredEntries.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Total Interactions"
            value={totalInteractions.toString()}
            
          />
          <MetricCard
            label="Guardrail Triggers"
            value={totalTriggers.toString()}
            
          />
          <MetricCard
            label="Passed Checks"
            value={passedChecks.toString()}
            
          />
          <MetricCard
            label="Escalations"
            value={escalations.toString()}
            
          />
        </div>

        {/* Event Type Filters */}
        <div className="flex items-center gap-2 mb-6">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm text-gray-700 font-medium">Filter:</span>
          {(['all', 'interaction', 'guardrail_trigger', 'escalation', 'config_change', 'api_call'] as EventTypeFilter[]).map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === type
                  ? 'bg-t3-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {type === 'all' ? 'All Events' : type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Audit Events</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'event' : 'events'}
            </p>
          </div>

          {filteredEntries.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={FileText}
                title={filterType === 'all' ? 'No audit events' : `No ${filterType} events`}
                description="Audit events will appear here as your agent operates"
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-warm border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Event Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Interaction ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEntries.map(entry => (
                    <tr key={entry.id} className="hover:bg-surface-warm transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {entry.timestamp.toDate().toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-600">
                          {entry.timestamp.toDate().toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            entry.eventType === 'escalation'
                              ? 'critical'
                              : entry.eventType === 'guardrail_trigger'
                              ? 'warning'
                              : 'primary'
                          }
                        >
                          {entry.eventType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-md truncate">
                          {entry.description}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">
                          {entry.actor || 'System'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {entry.interactionId ? (
                          <code className="text-xs text-gray-600 font-mono">
                            {entry.interactionId.substring(0, 8)}...
                          </code>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Compliance Notice */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Compliance & Data Retention</h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• All agent interactions and guardrail triggers are logged for audit purposes</li>
            <li>• Audit logs are retained for 90 days (Growth tier) or 1 year (Enterprise tier)</li>
            <li>• Logs include timestamps, event types, and actor information for full traceability</li>
            <li>• Export logs regularly for long-term compliance and archival requirements</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
